import { MIN_PASSWORD_LENGTH } from '@ofmos/shared/auth';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Button, FormField, Input } from '../../ui';
import { AuthLayout } from './AuthLayout';
import { authErrorMessage } from './authMessages';
import { FormError } from './FormError';
import { DEFAULT_BUTTON } from './LoginPage';
import { useRegister } from './useSession';

/** R8's own example sentence for a password that is too short, and R10's hint beside it. */
const PASSWORD_HINT = `At least ${String(MIN_PASSWORD_LENGTH)} characters.`;
export const PASSWORD_ERROR = `Choose a password with at least ${String(MIN_PASSWORD_LENGTH)} characters.`;

export interface RegisterFormProps {
  readonly name: string;
  readonly email: string;
  readonly password: string;
  readonly onNameChange: (value: string) => void;
  readonly onEmailChange: (value: string) => void;
  readonly onPasswordChange: (value: string) => void;
  readonly onSubmit: () => void;
  /** Beside the password field, which is the field it is about. */
  readonly passwordError?: string | undefined;
  /** Under the button: the server's refusal is about the instance, not about one field. */
  readonly formError?: string | undefined;
  readonly pending?: boolean | undefined;
}

/**
 * THE SAME CONSTRUCTION AS SIGN IN, with this form's three fields (E3, brief item 2): labels
 * above the fields, one default button at the form's width, the error beside the field it
 * belongs to or under the button, and the way back as a plain 13px link - never a second
 * capsule. Exported for `/dev/auth-states` for the reason `LoginForm` is.
 */
export function RegisterForm({
  name,
  email,
  password,
  onNameChange,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  passwordError,
  formError,
  pending = false,
}: RegisterFormProps) {
  const ready = name.trim() !== '' && email.trim() !== '' && password !== '';

  return (
    <form
      className="space-y-5"
      /**
       * THE BROWSER'S OWN BUBBLE IS TURNED OFF, and this is the round's one line about a web
       * pattern: `type="email"` makes Chromium refuse the submit and float its own tooltip
       * over the field ("Please include an '@' in the email address"), which is a bubble
       * nobody designed, in a typeface nobody chose, in one language. `noValidate` hands the
       * check back to the screen, which answers it beside the field the way the spec asks
       * (section 2 item 7). The field keeps `type="email"` for the phone keyboard.
       */
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <FormField label="Name">
        {(props) => (
          <Input
            {...props}
            autoComplete="name"
            placeholder="Your name"
            disabled={pending}
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
          />
        )}
      </FormField>

      <FormField label="Email">
        {(props) => (
          <Input
            {...props}
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            placeholder="name@example.com"
            disabled={pending}
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
          />
        )}
      </FormField>

      <FormField
        label="Password"
        hint={PASSWORD_HINT}
        {...(passwordError === undefined ? {} : { error: passwordError })}
      >
        {(props) => (
          <Input
            {...props}
            type="password"
            autoComplete="new-password"
            disabled={pending}
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
            invalid={passwordError !== undefined}
          />
        )}
      </FormField>

      {/*
        ONE LABEL FOR CREATING AN ACCOUNT (copy deck C3, Addendum 5): a verb phrase of two
        words without an article, the same label `AcceptInvitationPage` already carries, so
        the two ways into this installation name the act identically. The bootstrap spec that
        clicked the old name is updated in the same commit (it closes ledger E-W4-48).
      */}
      <Button
        type="submit"
        variant="push"
        block
        style={DEFAULT_BUTTON}
        disabled={!ready}
        loading={pending}
      >
        Create account
      </Button>

      {/* The same sentence in the same place as Sign In, on the same family (M3). */}
      <FormError message={formError} />

      <p className="text-2xs text-ink-subtle">
        <Link to="/login" className="text-accent">
          Sign in
        </Link>
      </p>
    </form>
  );
}

/**
 * Claiming a NEW instance: the first account, which becomes the admin.
 *
 * With `REGISTRATION_MODE=invite` (the default) the server refuses this as soon as one
 * account exists, and the refusal is shown as it is - there is nothing to work around, an
 * admin creates accounts from then on. That is requirement 10 made real instead of
 * documented.
 */
export function RegisterPage() {
  const navigate = useNavigate();
  const register = useRegister();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const error = register.error === null ? undefined : authErrorMessage(register.error);
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  return (
    <AuthLayout
      title="Set up this instance"
      subtitle="The first account becomes the administrator."
    >
      <RegisterForm
        name={name}
        email={email}
        password={password}
        onNameChange={setName}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        onSubmit={() => {
          if (tooShort) return;
          register.mutate(
            { email, password, name },
            { onSuccess: () => void navigate('/', { replace: true }) },
          );
        }}
        {...(tooShort ? { passwordError: PASSWORD_ERROR } : {})}
        {...(error === undefined ? {} : { formError: error })}
        pending={register.isPending}
      />
    </AuthLayout>
  );
}
